import { ThreeSixtyViewer } from './ThreeSixtyViewer';
import { ViewerMode, ViewerScreenDirection } from './Types';
import { ViewerScreen } from './ViewerScreen/ViewerScreen';

export class MouseController {
  private leftViewer: ViewerScreen;
  private rightViewer: ViewerScreen;
  private container: HTMLElement;
  private readonly boundOnMouseDown: (event: MouseEvent) => void;
  private readonly boundOnMouseMove: (event: MouseEvent) => void;
  private readonly boundOnMouseUp: (event: MouseEvent) => void;
  private readonly boundOnMouseWheel: (event: MouseEvent) => void;

  constructor(leftViewer: ViewerScreen, rightViewer: ViewerScreen, container: HTMLElement) {
    this.leftViewer = leftViewer;
    this.rightViewer = rightViewer;
    this.container = container;
    this.boundOnMouseDown = this.onMouseDown.bind(this);
    this.boundOnMouseMove = this.onMouseMove.bind(this);
    this.boundOnMouseUp = this.onMouseUp.bind(this);
    this.boundOnMouseWheel = this.onMouseWheel.bind(this);

    this.enable();
  }

  public enable() {
    this.container.addEventListener('mousedown', this.boundOnMouseDown, false);
    window.addEventListener('mouseup', this.boundOnMouseUp, false);
    window.addEventListener('mousemove', this.boundOnMouseMove, false);
    // Use { passive: false } to allow preventDefault() in wheel event handler
    this.container.addEventListener('wheel', this.boundOnMouseWheel, { passive: false });
  }

  public disable() {
    this.container.removeEventListener('mousedown', this.boundOnMouseDown, false);
    window.removeEventListener('mouseup', this.boundOnMouseUp, false);
    window.removeEventListener('mousemove', this.boundOnMouseMove, false);
    this.container.removeEventListener('wheel', this.boundOnMouseWheel, false);
  }

  private onMouseMove(event: MouseEvent): void {
    if (ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
      const { clickedViewer, otherViewer } = this.getViewerByMousePosition(event);
      clickedViewer.onMouseMove(event);
      if (!ThreeSixtyViewer.isLocked) otherViewer.onMouseMove(event, true);
    } else {
      this.rightViewer.onMouseMove(event);
    }
  }

  private onMouseDown(event: MouseEvent): void {
    if (ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
      const { clickedViewer, otherViewer } = this.getViewerByMousePosition(event);
      clickedViewer.onMouseDown(event);
      if (!ThreeSixtyViewer.isLocked) otherViewer.onMouseDown(event, true);
    } else {
      this.rightViewer.onMouseDown(event);
    }
  }

  private onMouseWheel(event: WheelEvent): void {
    if (ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
      const { clickedViewer, otherViewer } = this.getViewerByMousePosition(event);
      clickedViewer.onMouseWheel(event);
      if (!ThreeSixtyViewer.isLocked) otherViewer.onMouseWheel(event);
    } else {
      this.rightViewer.onMouseWheel(event);
    }
  }

  private onMouseUp(event: MouseEvent): void {
    this.leftViewer.onMouseUp();
    this.rightViewer.onMouseUp();
  }

  public addNewObservation(
    event: MouseEvent,
    newObservationImage: (rawImage: string) => void,
  ): { horizontalAngle: number; verticalAngle: number; isRightViewer: boolean } {
    const { clickedViewer } = this.getViewerByMousePosition(event);
    const mouse = this.getMousePositionByViewer(clickedViewer, event);
    return {
      ...clickedViewer.addNewObservation(mouse, newObservationImage),
      isRightViewer: clickedViewer === this.rightViewer,
    };
  }
  private getMousePositionByViewer(
    viewer: ViewerScreen,
    event: MouseEvent,
  ): { x: number; y: number } {
    const { clientX, clientY } = event;
    const { innerWidth, innerHeight } = window;
    const mouse = { x: 0, y: 0 };
    if (ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
      if (clientX < innerWidth / 2) {
        mouse.x = (clientX / (innerWidth / 2)) * 2 - 1;
        mouse.y = -(clientY / innerHeight) * 2 + 1;
      } else {
        mouse.x = ((clientX - innerWidth / 2) / (innerWidth / 2)) * 2 - 1;
        mouse.y = -(clientY / innerHeight) * 2 + 1;
      }
    } else if (ThreeSixtyViewer.viewerMode === ViewerMode.HALF_SCREEN) {
      mouse.x = ((clientX - innerWidth / 2) / (innerWidth / 2)) * 2 - 1;
      mouse.y = -(clientY / innerHeight) * 2 + 1;
    } else {
      mouse.x = (clientX / innerWidth) * 2 - 1;
      mouse.y = -(clientY / innerHeight) * 2 + 1;
    }
    return mouse;
  }

  private getViewerByMousePosition(event: MouseEvent): {
    clickedViewer: ViewerScreen;
    otherViewer: ViewerScreen;
  } {
    if (ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
      if (event.clientX < window.innerWidth / 2) {
        return { clickedViewer: this.leftViewer, otherViewer: this.rightViewer };
      } else if (event.clientX >= window.innerWidth / 2) {
        return { clickedViewer: this.rightViewer, otherViewer: this.leftViewer };
      } else {
        return { clickedViewer: this.rightViewer, otherViewer: this.leftViewer };
      }
    } else {
      return { clickedViewer: this.rightViewer, otherViewer: this.leftViewer };
    }
  }
}
